import { CSSProperties } from 'react';
import { useDateNow, useReduxState } from 'hooks';
import { DisplayFormattedDate } from 'components/common';
import { Icon } from 'components/common/icons';

interface Props {
  deadline: string;
  isActive?: boolean;
  isEdited?: boolean;
  noneMessage?: string;
  noStatus?: boolean;
  showIcon?: boolean;
  style?: CSSProperties;
}

const CourseUserDeadline = ({
  deadline,
  isActive = true,
  isEdited,
  noneMessage,
  noStatus = false,
  showIcon = true,
  style
}: Props) => {
  const { dateNow } = useDateNow();

  const { messages, theme } = useReduxState();

  const expired = deadline && dateNow > Date.parse(deadline);

  const status = noStatus
    ? 'active'
    : !isActive
    ? 'inactive'
    : expired
    ? 'expired'
    : 'active';

  const options = {
    active: {
      additionalClass: '',
      tooltip: null
    },
    inactive: {
      additionalClass: 'inactive',
      tooltip: messages.commonCompleted
    },
    expired: {
      additionalClass: 'expired',
      tooltip: messages.commonExpired
    }
  }[status];

  return (
    <div className="deadline" style={style}>
      {deadline ? (
        <div
          aria-label={messages.courseTargetSchedule + options.tooltip}
          className={`value ${options.additionalClass}`}
          data-tooltip={options.tooltip}
        >
          {showIcon ? (
            <div className="icon">
              <Icon icon={['far', 'clock']} />
            </div>
          ) : null}

          <DisplayFormattedDate date={deadline} />
        </div>
      ) : (
        <div className="none">
          {noneMessage !== undefined
            ? noneMessage
            : messages.courseTargetScheduleNone}
        </div>
      )}

      {isEdited ? (
        <div
          className="edited"
          aria-label={messages.courseTargetScheduleEdited}
          data-tooltip={messages.courseTargetScheduleEdited}
        >
          <Icon icon="pen" />
        </div>
      ) : null}

      <style jsx>{`
        .deadline {
          align-items: center;
          display: inline-flex;
          font-size: 1.5rem;
        }
        .value {
          align-items: center;
          display: inline-flex;
        }
        .inactive {
          color: #bbb;
        }
        .expired {
          color: ${theme.dangerColor};
        }
        .none {
          color: ${theme.textColorLighter};
          font-size: 1.3rem;
        }
        .icon {
          font-size: 1.3rem;
          margin-right: 5px;
          position: relative;
        }
        .edited {
          background-color: ${theme.warningLightColor};
          border-radius: 100px;
          color: ${theme.warningLightTextColor};
          display: inline-block;
          font-size: 0.8rem;
          margin: 3px 0 3px 5px;
          padding: 3px 0;
          text-align: center;
          width: 16px;
          height: 16px;
        }
      `}</style>
    </div>
  );
};

export default CourseUserDeadline;
